import * as THREE from 'three/webgpu';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { DefaultEntitySelectionHandler } from './DefaultEntitySelectionHandler';

/**
 * 基准面模式实体选择处理器。
 *
 * 职责：
 * 1. 仅在基准面命令期间处理拾取与移入高亮；
 * 2. 直接把命中的特征点/线/面或正式基准点/轴/面写入基准面参考选择；
 * 3. 禁止默认模式下的拖拽、框选与普通语义选择流程。
 */
export class ReferencePlaneEntitySelectionHandler extends DefaultEntitySelectionHandler {
    /**
     * @param entitySelectionManager 实体选择管理器。
     */
    public constructor(entitySelectionManager: EntitySelectionManager) {
        super(entitySelectionManager);
    }

    /**
     * 处理鼠标按下。
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标移动。
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        if (payload.buttons !== 0) {
            this.clearHoverState();
            return;
        }
        this.queueHoverInput(payload.clientX, payload.clientY);
    }

    /** @inheritdoc 使用当前模式的候选规则消费最新悬停坐标。 */
    public override consumeHoverInput(clientX: number, clientY: number): void {
        const hoveredPick = this.pickEntityAt(
            clientX,
            clientY,
            this.collectReferencePlaneSelectionTargets(),
        );
        this.updateHoverState(this.resolveHoverPickWithSelectionExclusion(hoveredPick));
    }

    /**
     * 处理鼠标抬起。
     * @param payload 原始事件。
     */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标点击。
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.getSuppressNextClick()) {
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        const rawPicked = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectReferencePlaneSelectionTargets(),
        );
        const picked = this.resolveClickSelectionPick(rawPicked);
        if (!picked) {
            return;
        }

        //------ 基准轴作为基准面引用 开始------
        if (
            !isFeatureFace(picked.object)
            && !isFeatureLine(picked.object)
            && !isFeaturePoint(picked.object)
            && !isDatumAxis(picked.object)
            && !isDatumPoint(picked.object)
            && !isDatumPlaneObject(picked.object)
        ) {
            return;
        }
        //------ 基准轴作为基准面引用 结束------

        const viewer = this.entitySelectionManager.renderer;
        const accepted = viewer.referencePlaneController.addSelectionByObject(picked.object);
        if (!accepted) {
            const rejectionMessage = viewer.referencePlaneController.getViewModel().state.preview.error
                ?? '当前实体不支持作为基准面参考';
            viewer.drawingNoticeCallback?.(rejectionMessage, 'warning');
            return;
        }

        viewer.referencePlaneController.syncSelectionVisuals();
    }

    /**
     * 收集框选候选对象。
     * @returns 空候选列表。
     */
    protected override collectBoxSelectionTargets(): THREE.Object3D[] {
        return [];
    }

    /**
     * 收集测量叠加层点击候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureClickSelectionTargets(): THREE.Object3D[] {
        return this.collectReferencePlaneSelectionTargets();
    }

    /**
     * 收集测量叠加层移入候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureHoverSelectionTargets(): THREE.Object3D[] {
        return this.collectReferencePlaneSelectionTargets();
    }

    /**
     * 收集基准面模式的可拾取对象。
     * @returns 可参与拾取的对象列表。
     */
    private collectReferencePlaneSelectionTargets(): THREE.Object3D[] {
        const featureAndDatumTargets = this.collectDatumReferenceInteractionTargets(this.collectClickSelectionTargets()).filter((candidate) => (
            isFeatureFace(candidate)
            || isFeatureLine(candidate)
            || isFeaturePoint(candidate)
            || isDatumAxis(candidate)
            || isDatumPoint(candidate)
            || isDatumPlaneObject(candidate)
        ));
        return this.mergeImportedTopologySelectionTargets(featureAndDatumTargets);
    }
}
